/**
 * Cross-tab sync for the phone panel over a BroadcastChannel. Two events ride
 * it: `sms-sent` (this tab sent an SMS — siblings showing the thread refetch it
 * instead of relying on the socket's `sms_status` reconcile, which can't tell
 * another tab's send apart from this tab's while a send is in flight) and
 * `call-ended` (a call finished on the telephony LEADER tab — `lastEnded` is
 * leader-only, so follower tabs would otherwise never refresh the just-logged
 * call into their thread). Same-browser only by nature — cross-device sync
 * stays on the socket path.
 */

/** Thread the sent message belongs to; either key may be absent. */
export interface SmsSentTarget {
  conversationId?: string
  phone?: string
}

type SyncMessage =
  | ({ type: 'sms-sent' } & SmsSentTarget)
  | { type: 'call-ended' }

const CHANNEL_NAME = 'phone-panel-sync'

let channel: BroadcastChannel | null = null

/** Lazily create the shared channel; null where the API is unavailable (e.g.
 *  jsdom). A tab never receives its own posts, so one channel serves both
 *  directions. */
function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  channel ??= new BroadcastChannel(CHANNEL_NAME)
  return channel
}

function messageType(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined
  const { type } = data as { type?: unknown }
  return typeof type === 'string' ? type : undefined
}

function subscribe(
  type: SyncMessage['type'],
  handler: (data: unknown) => void,
): () => void {
  const syncChannel = getChannel()
  if (!syncChannel) return () => undefined
  const onMessage = (event: MessageEvent<unknown>) => {
    if (messageType(event.data) === type) handler(event.data)
  }
  syncChannel.addEventListener('message', onMessage)
  return () => syncChannel.removeEventListener('message', onMessage)
}

/** Tell sibling tabs an SMS was just sent to this thread. */
export function broadcastSmsSent(target: SmsSentTarget): void {
  const message: SyncMessage = { type: 'sms-sent', ...target }
  getChannel()?.postMessage(message)
}

/** Listen for sends from sibling tabs; returns the unsubscribe. */
export function subscribeSmsSent(
  handler: (target: SmsSentTarget) => void,
): () => void {
  return subscribe('sms-sent', (data) => {
    const { conversationId, phone } = data as SmsSentTarget
    handler({ conversationId, phone })
  })
}

/** Tell sibling tabs a call just ended on this (leader) tab. */
export function broadcastCallEnded(): void {
  const message: SyncMessage = { type: 'call-ended' }
  getChannel()?.postMessage(message)
}

/** Listen for call-ends from sibling tabs; returns the unsubscribe. */
export function subscribeCallEnded(handler: () => void): () => void {
  return subscribe('call-ended', () => handler())
}
